// project imports
import {
    processEntityInput as processEntityInputMain,
    processEntityOutput as processEntityOutputMain
} from 'store/serializers/fragments/main';
import { getIdFromObj, getIriFromObj } from 'utils/processors';
import { sortTasksByParentPosition } from './tree';

const getTaskIdFromIri = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const match = value.match(/\/tplmgmt\/tasks\/(\d+)$/);
    return match ? Number(match[1]) : null;
};

const getTaskIri = (value) => {
    const iri = getIriFromObj(value);
    if (iri) {
        return iri;
    }

    const id = getIdFromObj(value);
    if (id !== undefined && id !== null && `${id}`.trim()) {
        return `/tplmgmt/tasks/${id}`;
    }

    return null;
};

const processEmbeddedEntityParentInput = (parentValue, fallbackParent = null) => {
    const resolvedParent = parentValue ?? fallbackParent;

    if (resolvedParent === undefined) {
        return undefined;
    }

    if (resolvedParent === null) {
        return null;
    }

    const parentIdFromIri = getTaskIdFromIri(resolvedParent);
    if (parentIdFromIri !== null) {
        return parentIdFromIri;
    }

    const parentId = getIdFromObj(resolvedParent);
    if (parentId !== undefined && parentId !== null && `${parentId}`.trim()) {
        return parentId;
    }

    const parentIri = getIriFromObj(resolvedParent);
    if (parentIri) {
        return parentIri;
    }

    return resolvedParent;
};

export const processEntityInput = (values) => ({
    ...values,
    ...processEntityInputMain(values),
    allowsWorkOrderCreation: 'allowsWorkOrderCreation' in (values || {}) ? Boolean(values?.allowsWorkOrderCreation) : undefined,
    requiresExpectedValueOnCompletion:
        'requiresExpectedValueOnCompletion' in (values || {}) ? Boolean(values?.requiresExpectedValueOnCompletion) : undefined,
    requiresContactsOnCreation: 'requiresContactsOnCreation' in (values || {}) ? Boolean(values?.requiresContactsOnCreation) : undefined,
    children:
        'children' in (values || {}) && Array.isArray(values?.children)
            ? sortTasksByParentPosition(values.children.map((child) => processEntityInput(child)))
            : undefined
});

export const processEmbeddedEntityInput = (values, { businessOpportunity = null, workOrder = null, parentTask = null } = {}) => ({
    ...processEntityInput(values),
    parent: 'parent' in (values || {}) || parentTask !== null ? processEmbeddedEntityParentInput(values?.parent, parentTask) : undefined,
    businessOpportunityId:
        values?.businessOpportunityId ?? getIdFromObj(values?.businessOpportunity) ?? getIdFromObj(businessOpportunity) ?? undefined,
    workOrderId: values?.workOrderId ?? getIdFromObj(values?.workOrder) ?? getIdFromObj(workOrder) ?? undefined,
    businessOpportunity: values?.businessOpportunity ?? businessOpportunity ?? undefined,
    workOrder: values?.workOrder ?? workOrder ?? undefined
});

export const processEntityOutput = (values) => ({
    ...values,
    ...processEntityOutputMain(values),
    allowsWorkOrderCreation: 'allowsWorkOrderCreation' in (values || {}) ? Boolean(values?.allowsWorkOrderCreation) : undefined,
    requiresExpectedValueOnCompletion:
        'requiresExpectedValueOnCompletion' in (values || {}) ? Boolean(values?.requiresExpectedValueOnCompletion) : undefined,
    requiresContactsOnCreation: 'requiresContactsOnCreation' in (values || {}) ? Boolean(values?.requiresContactsOnCreation) : undefined,
    id: undefined,
    path: undefined,
    level: undefined,
    root: undefined,
    children: undefined,
    parent: 'parent' in (values || {}) ? getTaskIri(values?.parent) : undefined,
    positionByParent: 'positionByParent' in (values || {}) ? values?.positionByParent ?? null : undefined,
    businessOpportunity: 'businessOpportunity' in (values || {}) ? getIriFromObj(values.businessOpportunity) : undefined,
    workOrder: 'workOrder' in (values || {}) ? getIriFromObj(values.workOrder) : undefined
});
